import type {ReactElement} from 'react';
import React from 'react';
import type {ItemBorderObject} from '@PosterWhiteboard/classes/item-border.class';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import type {DropdownItemStorage} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {BaseBorderThicknessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-border-thickness-option';
import {BaseBorderColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-border-color-option';

interface BaseItemBorderOptionProps {
  borderObject: ItemBorderObject;
  showStrokeBorderOption?: boolean;
  isForLayout?: boolean;

  onClickColorFill(): void;

  onChangeBorder(id: BorderType): void;

  updateThickness(solidBorderThickness: number, undoable?: boolean): void;
}

export function BaseItemBorderOption({isForLayout = false, showStrokeBorderOption = false, ...props}: BaseItemBorderOptionProps): ReactElement {
  const onClickHandler = (clickedId: BorderType): void => {
    if (getSelectedItemId() === clickedId) {
      return;
    }
    props.onChangeBorder(Number(clickedId));
  };

  const getDropdownItems = (): Array<DropdownItemStorage> => {
    const dropdownItems = [{id: BorderType.NONE, text: window.i18next.t('pmwjs_none')}];
    if (showStrokeBorderOption) {
      dropdownItems.push({id: BorderType.STROKE_BORDER, text: window.i18next.t('pmwjs_outline')});
    }

    dropdownItems.push(
      {id: BorderType.RECTANGLE_BORDER, text: window.i18next.t('pmwjs_rectangle_border')},
      {id: BorderType.ROUNDED_BORDER, text: window.i18next.t('pmwjs_rounded_border')}
    );
    return dropdownItems;
  };

  const getBorderThicknessOption = (): ReactElement => {
    return (
      <BaseBorderThicknessOption
        solidBorderThickness={props.borderObject.solidBorderThickness}
        updateThickness={(value: number, undoable = false): void => {
          props.updateThickness(value, undoable);
        }}
        isForLayout={isForLayout}
        borderType={props.borderObject.solidBorderType}
      />
    );
  };

  const getBorderColorOption = (): ReactElement => {
    return (
      <BaseBorderColorOption
        solidBorderColor={props.borderObject.solidBorderColor}
        onClickColorFill={(): void => {
          props.onClickColorFill();
        }}
      />
    );
  };

  const getSubOptions = (): ReactElement | null => {
    if (!showSubOption()) {
      return null;
    }
    return (
      <>
        {getBorderColorOption()}
        {getBorderThicknessOption()}
      </>
    );
  };

  const getSelectedItemId = (): BorderType => {
    return props.borderObject.solidBorderType;
  };

  const showSubOption = (): boolean => {
    return getSelectedItemId() !== BorderType.NONE;
  };

  return (
    <PosterEditingBaseDropdownOption
      id="item-border-contextual-option"
      label={window.i18next.t('pmwjs_border')}
      onClick={onClickHandler}
      dropdownItems={getDropdownItems()}
      selectedItemId={getSelectedItemId()}
    >
      {getSubOptions()}
    </PosterEditingBaseDropdownOption>
  );
}
